import axios from "axios";
import store from "../../store/index";
import OrganizationModal from './organizationModel.vue';

function extractApiErrorMessage(error, fallback) {
  const data = error.response && error.response.data;
  if (!data) {
    return error.message || fallback;
  }
  if (data.detail) {
    return data.detail;
  }
  if (data.errors) {
    const parts = [];
    Object.keys(data.errors).forEach(key => {
      const val = data.errors[key];
      if (Array.isArray(val)) {
        parts.push(...val);
      } else if (val) {
        parts.push(String(val));
      }
    });
    if (parts.length) {
      return parts.join(' ');
    }
  }
  if (data.title) {
    return data.title;
  }
  if (data.message) {
    return data.message;
  }
  return error.message || fallback;
}

export default {
  name: 'Organization',

  components: {
    OrganizationModal
  },

  data() {
    return {
      baseUrl: process.env.VUE_APP_Service_URL,
      idToken: store.getters.getIdToken,
      isLoading: false,
      orgs: [],
      currentPage: 1,
      pageSize: 10,
      totalCount: 0,
      sortProperty: 'organizationName',
      sortOrder: 1,
      expandedCells: [],
      openActionMenuId: null,
      actionMenuPos: { top: 0, left: 0 },
      filters: {
        organizationName: '',
        isActive: '',
      },
      }
  },

  computed: {
    totalPages() {
      return Math.max(Math.ceil(this.totalCount / this.pageSize), 1);
    },

    pageStart() {
      return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },

    pageEnd() {
      return Math.min(this.currentPage * this.pageSize, this.totalCount);
    },

    activeOrg() {
      return this.orgs.find(o => o.id === this.openActionMenuId) || null;
    },

    actionMenuStyle() {
      return {
        top: `${this.actionMenuPos.top}px`,
        left: `${this.actionMenuPos.left}px`,
      };
    },
  },

  methods: {
    async fetchOrganizations(page) {
      const targetPage = page || this.currentPage;
      this.isLoading = true;
      try {
         const params = {
          pageindex: targetPage,
          pagesize: this.pageSize,
          SortProperty: this.sortProperty,
          SortOrder: this.sortOrder,
        };

        if (this.filters.organizationName && this.filters.organizationName.trim()) {
          params.OrganizationName = this.filters.organizationName.trim();
        }
        if (this.filters.isActive !== '') {
          params.IsActive = this.filters.isActive === 'true';
        }

        const response = await axios.get(`${this.baseUrl}/admin/orginization/getall`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.idToken}`,
          },
          params
        });

        const entities = response.data.entities || [];
        this.totalCount = response.data.totalCount || 0;

        this.orgs = entities.map(e => ({
          id: e.organizationId,
          name: e.organizationName,
          url: e.url,
          active: e.isActive,
          rootEmail: e.rootUserEmail,
          rootName: e.rootUserName,
          connectionString: e.connectionString,
          welcomeText: e.welcomeText,
          logoUrl: e.logoUrl,
        }));

        this.currentPage = targetPage;

        if (this.orgs.length === 0 && this.currentPage > 1) {
          this.fetchOrganizations(this.currentPage - 1);
          return;
        }

      } catch (error) {
        const message = extractApiErrorMessage(error, 'Failed to fetch organizations');
        alert(`Error: ${message}`);
        console.error('Error fetching organizations:', error);
      } finally {
        this.isLoading = false;
      }
    },

    openAddOrg() {
      this.$refs.orgModal.openOrganizationModal();
    },

    openEditOrg(org) {
      this.closeActionMenu();
      this.$refs.orgModal.openOrganizationModal(org);
    },

    onOrgSaved() {
      this.fetchOrganizations(this.currentPage);
    },

    async deleteOrganization(org) {
      this.closeActionMenu();
      if (!confirm(`Delete organization "${org.name}"? This cannot be undone.`)) {
        return;
      }

      try {
        await axios.delete(`${this.baseUrl}/admin/orginization/delete?id=${encodeURIComponent(org.id)}`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.idToken}`
          }
        });
        alert('Organization deleted successfully');
        this.fetchOrganizations(this.currentPage);
      } catch (error) {
        const message = extractApiErrorMessage(error, 'Failed to delete organization');
        alert(`Error: ${message}`);
        console.error('Error deleting organization:', error);
      }
    },

    cellKey(id, field) {
      return `${id}:${field}`;
    },

    isCellExpanded(id, field) {
      return this.expandedCells.indexOf(this.cellKey(id, field)) !== -1;
    },

    toggleCell(id, field) {
      const key = this.cellKey(id, field);
      const idx = this.expandedCells.indexOf(key);
      if (idx === -1) {
        this.expandedCells.push(key);
      } else {
        this.expandedCells.splice(idx, 1);
      }
    },

    // ── Actions (⋮) dropdown: positioned via getBoundingClientRect since it's
    // teleported to <body> and no longer inside the scrollable/clipped table ──
    toggleActionMenu(id, event) {
      if (this.openActionMenuId === id) {
        this.closeActionMenu();
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      const menuWidth = 110; // matches .action-menu min-width
      this.actionMenuPos = {
        top: rect.bottom + 4,
        left: rect.right - menuWidth,
      };
      this.openActionMenuId = id;
      window.addEventListener('scroll', this.closeActionMenu, true);
      window.addEventListener('resize', this.closeActionMenu);
    },

    closeActionMenu() {
      this.openActionMenuId = null;
      window.removeEventListener('scroll', this.closeActionMenu, true);
      window.removeEventListener('resize', this.closeActionMenu);
    },

  async toggleOrgStatus(org) {
  if (!org) return;
  this.closeActionMenu();
  const nextState = !org.active;
  const verb = nextState ? 'activate' : 'deactivate';
  if (!confirm(`Are you sure you want to ${verb} "${org.name}"?`)) {
    return;
  }

  try {
    await axios.patch(
      `${this.baseUrl}/admin/orginization/${verb}?id=${encodeURIComponent(org.id)}`,
      null,
      {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.idToken}`
        }
      }
    );
    alert(`Organization ${nextState ? 'activated' : 'deactivated'} successfully`);
    this.fetchOrganizations(this.currentPage);
  } catch (error) {
    const message = extractApiErrorMessage(error, `Failed to ${verb} organization`);
    alert(`Error: ${message}`);
    console.error(`Error trying to ${verb} organization:`, error);
  }
},

    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }
      this.fetchOrganizations(page);
    },

    prevPage() {
      this.goToPage(this.currentPage - 1);
    },

    nextPage() {
      this.goToPage(this.currentPage + 1);
    },
    searchOrganizations() {
      this.currentPage = 1;
      this.fetchOrganizations(1);
    },
  },

  beforeUnmount() {
    window.removeEventListener('scroll', this.closeActionMenu, true);
    window.removeEventListener('resize', this.closeActionMenu);
  },

  mounted() {
    this.fetchOrganizations(1);
  },
}